<template>
  <BasePopup
    ref="popup"
    title="What's new and features"
    class="changelog-popup"
  >
    <div ref="containerRef" class="changelog-popup__container">
      <transition name="fade" mode="out-in" @enter="onTransitionEnd">
        <div v-if="selectedItem" key="detail" class="changelog-popup__details">
          <Item :item="selectedItem" :isDetail="true" :onBack="goBack" />
        </div>
        <div v-else key="list">
          <Item
            v-for="item in view.data"
            :key="item.id"
            :item="item"
            @click="selectItem(item)"
          />
        </div>
      </transition>
    </div>
  </BasePopup>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { ChangelogView } from '@/models/changelog'
import BasePopup from '@/components/ui/BasePopup.vue'
import Item from './Item.vue'
import { backend } from '@/services/backend'
import { Changelog } from '@/models/interfaces'
import * as Mixpanel from '@/analytics/mixpanel/mixpanel'
const props = defineProps<{
  view: ChangelogView
}>()

// Note: in vue 3.5 we need to switch to useTemplateRef
const popup = ref<InstanceType<typeof BasePopup> | null>(null)

const selectedId = ref<string | null>(null)
const lastScrollPosition = ref<number>(0)
const containerRef = ref<HTMLElement | null>(null)

async function selectItem(item: Changelog): Promise<void> {
  // Store scroll position before any transition
  if (containerRef.value) {
    lastScrollPosition.value = containerRef.value.scrollTop
  }

  selectedId.value = item.id

  Mixpanel.trackChangelogItemClick(item)

  // If the item is historical or already read, we don't need to mark it as read
  if (item.is_historical || item.is_read) {
    return
  }

  await backend.markReadChangelog(item.id)

  // Update the item in the view
  item.is_read = true
  props.view.refreshIfNeeded(true)
}

const selectedItem = computed(() => {
  return props.view.data.find((item) => item.id === selectedId.value)
})

function show(): void {
  popup.value?.show()
}

function hide(): void {
  popup.value?.hide()
}

function goBack(): void {
  selectedId.value = null
}

// Handle transition end to restore scroll position
function onTransitionEnd(): void {
  if (containerRef.value) {
    // If we're in list view (selectedId is null), restore scroll position
    // Otherwise (in detail view), scroll to top
    containerRef.value.scrollTo({
      top: selectedId.value === null ? lastScrollPosition.value : 0,
    })
  }
}

defineExpose({
  show,
  hide,
})
</script>

<style lang="scss" scoped>
.changelog-popup {
  --input-border-color-hover: #{$c-border-5};
  --popup-width: 75vw;
  --popup-padding: 0;

  :deep(.popup__content) {
    border: 1px solid $c-border-4;
    width: min(var(--popup-width), $w-max-content-width);
  }

  :deep(.popup__title) {
    margin-bottom: 0;

    @media only screen and (max-width: $w-mobile-s) {
      font-size: 24px;
      line-height: 32px;
    }
  }

  :deep(.popup__close) {
    top: 36px;
  }

  @media only screen and (max-width: $w-tablet-s) {
    --popup-width: 100vw;

    :deep(.popup__flex-box) {
      align-items: end;
    }

    :deep(.popup__content) {
      margin: 0;
      border-radius: 18px 18px 0 0;
      height: 90svh;
      max-height: inherit;
    }

    background-color: rgba(20, 28, 49, 0.3);
  }

  :deep(.popup__title-box) {
    padding: 30px 48px;
    margin-bottom: 0;
    text-align: center;
    border-color: $c-border;
    background: $c-bg;
  }

  &__container {
    max-height: 70vh;
    overflow-y: auto;
    padding: 80px;

    @media only screen and (max-width: $w-tablet-s) {
      padding: 40px;
    }
  }

  &__details {
    min-height: 60svh;
  }

  &__container {
    @media only screen and (max-width: $w-tablet-s) {
      padding: 24px;
    }
  }
}

.night-mode .changelog-popup {
  --input-border-color: #{$c-black-9};
  --input-border-color-hover: #{$c-border-6};

  :deep(.popup__content) {
    border-color: $c-dark-3;
  }

  :deep(.popup__title-box) {
    border-color: $c-dark-3;
    background: $c-dark-2;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
